import React, { useState, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import { apiGet } from '../api';
import fyLogo from '../assets/logo.png';
import gtitLogo from '../assets/gtit.png';

// Rates are FIXED (set on the server too) — not editable here.
const RATES = { audioPer1000Min: 0.5, videoPer1000Min: 2.0, currency: 'USD' };

// Invoice parties — GolpoStream (issuer) bills Global Trust IT (payer) for media
// server usage. Edit address / contact lines as needed.
const SELLER = {
  name: 'GolpoStream',
  tagline: 'Live Streaming Media Services',
  lines: ['Singapore City, Singapore'], // TODO: confirm GolpoStream's registered address
  web: 'trustlive.online',
  logo: fyLogo,
};
const BILL_TO = {
  name: 'Global Trust IT',
  lines: ['Dhaka, Bangladesh'],
  web: 'gtit.dev',
  logo: gtitLogo,
};
// Where Global Trust IT sends payment — bank transfer only.
const PAYMENT = {
  bankName: 'DBBL PLC',
  accountName: 'GLOBAL TRUST IT',
  bankAccount: '2931100452418',
  branch: 'Baraipara Branch',
  routing: '090260418',
  swift: 'DBBLBDDH',
};
const PAY_TERMS_DAYS = 7;

const toMin = (sec) => (sec || 0) / 60;
const fmtMin = (sec) => toMin(sec).toLocaleString(undefined, { maximumFractionDigits: 1 });
const money = (v) => `${RATES.currency} ${Number(v || 0).toFixed(2)}`;

const Bill = () => {
  const [cycles, setCycles] = useState([]);
  const [cycle, setCycle] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1) Load the list of billing cycles (newest first) for the history picker.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet('/api/billing/cycles');
        setCycles(res.cycles || []);
        setCycle(res.current || (res.cycles?.[0]?.cycle ?? ''));
      } catch (e) {
        setError(e.message || 'Failed to load billing cycles.');
        setLoading(false);
      }
    })();
  }, []);

  // 2) Load the invoice for the selected cycle.
  const loadCycle = useCallback(async (key) => {
    if (!key) return;
    setLoading(true); setError(null);
    try {
      setData(await apiGet(`/api/billing/monthly?cycle=${key}`));
    } catch (e) {
      setError(e.message || 'Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCycle(cycle); }, [cycle, loadCycle]);

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPDF = async () => {
    if (!data) return;
    setPdfBusy(true);
    try {
      const doc = await buildInvoicePDF(data);
      doc.save(`GolpoStream-Invoice-${data.cycle}.pdf`);
    } catch (e) {
      alert('Failed to build PDF: ' + (e.message || e));
    } finally {
      setPdfBusy(false);
    }
  };

  const cardStyle = {
    flex: '1 1 160px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12, padding: '14px 16px',
  };
  const labelStyle = { color: 'rgba(255,255,255,0.45)', fontSize: 12, marginBottom: 6 };

  const s = data?.summary;

  return (
    <div className="dark-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>Media Server Invoice</h2>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 4 }}>
            Monthly cycle — 28th to 28th · Audio = $0.50 · Video = $2.00 · camera-on = video rate, otherwise audio rate · Free 10,000 minutes per month.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select
            className="dark-input"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
            style={{ minWidth: 200 }}
          >
            {cycles.map((c) => (
              <option key={c.cycle} value={c.cycle} style={{ background: '#1a1a2e' }}>
                {c.label}{c.current ? ' (current)' : c.finalized ? ' (final)' : ''}
              </option>
            ))}
          </select>
          <button
            onClick={downloadPDF}
            disabled={!data || loading || pdfBusy}
            style={{
              background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', color: '#fff',
              border: 'none', borderRadius: 8, padding: '9px 16px',
              cursor: data && !loading && !pdfBusy ? 'pointer' : 'not-allowed', fontSize: 13,
              opacity: data && !loading && !pdfBusy ? 1 : 0.6, fontWeight: 600,
            }}
          >
            <i className="fas fa-file-pdf" style={{ marginRight: 6 }} />
            {pdfBusy ? 'Building…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {error && <div className="dark-error" style={{ marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div className="dark-loading"><div className="spinner" /><p>Loading invoice...</p></div>
      ) : data ? (
        <>
          {/* Parties + period banner */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 12, padding: '14px 16px', marginBottom: 16,
            display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <img src={SELLER.logo} alt="" style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'cover' }} />
              <div>
                <div style={labelStyle}>From</div>
                <div style={{ fontWeight: 700 }}>{SELLER.name}</div>
                {SELLER.lines.map((l) => <div key={l} style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{l}</div>)}
                <div style={{ color: '#ff8fa3', fontSize: 12 }}>{SELLER.web}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <img src={BILL_TO.logo} alt="" style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'cover' }} />
              <div>
                <div style={labelStyle}>Bill to</div>
                <div style={{ fontWeight: 700 }}>{BILL_TO.name}</div>
                {BILL_TO.lines.map((l) => <div key={l} style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{l}</div>)}
                <div style={{ color: '#7dd3fc', fontSize: 12 }}>{BILL_TO.web}</div>
              </div>
            </div>
            <div>
              <div style={labelStyle}>Billing period</div>
              <div style={{ fontWeight: 700 }}>{data.label}</div>
              <div style={{
                display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 700,
                padding: '2px 8px', borderRadius: 20,
                color: data.finalized ? '#4ade80' : '#fbbf24',
                background: data.finalized ? 'rgba(74,222,128,0.12)' : 'rgba(251,191,36,0.12)',
                border: `1px solid ${data.finalized ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.3)'}`,
              }}>
                <i className={`fas ${data.finalized ? 'fa-lock' : 'fa-circle-notch'}`} style={{ marginRight: 5 }} />
                {data.finalized ? 'Finalized' : 'Live · current cycle'}
              </div>
              {data.finalized && (
                <div style={{
                  display: 'inline-block', marginTop: 6, marginLeft: 6, fontSize: 11, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 20,
                  color: data.paymentStatus === 'paid' ? '#4ade80' : '#f87171',
                  background: data.paymentStatus === 'paid' ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                  border: `1px solid ${data.paymentStatus === 'paid' ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}`,
                }}>
                  <i className={`fas ${data.paymentStatus === 'paid' ? 'fa-check-circle' : 'fa-clock'}`} style={{ marginRight: 5 }} />
                  {data.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
              <div>Audio: {money(RATES.audioPer1000Min)} / 1000 min</div>
              <div>Video: {money(RATES.videoPer1000Min)} / 1000 min</div>
            </div>
          </div>

          {/* Summary cards */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
            <div style={cardStyle}>
              <div style={labelStyle}><i className="fas fa-microphone" style={{ marginRight: 6 }} />Audio minutes</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMin(s.audioSeconds)}</div>
              <div style={{ color: '#4ade80', fontSize: 13, marginTop: 4 }}>{money(s.audioCost)}</div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}><i className="fas fa-video" style={{ marginRight: 6 }} />Video minutes</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMin(s.videoSeconds)}</div>
              <div style={{ color: '#c4b5fd', fontSize: 13, marginTop: 4 }}>{money(s.videoCost)}</div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}><i className="fas fa-users" style={{ marginRight: 6 }} />Sessions</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{(s.sessions || 0).toLocaleString()}</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 4 }}>host sessions</div>
            </div>
            <div style={{ ...cardStyle, background: 'linear-gradient(135deg,rgba(255,77,109,0.16),rgba(255,45,85,0.10))', borderColor: 'rgba(255,77,109,0.3)' }}>
              <div style={labelStyle}><i className="fas fa-file-invoice-dollar" style={{ marginRight: 6 }} />Total due</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#ff6b81' }}>{money(s.totalCost)}</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 4 }}>{data.label}</div>
            </div>
          </div>

          {/* Line-item summary (no per-user list) */}
          <h3 style={{ margin: '0 0 10px' }}>Charges</h3>
          <div className="dark-table-wrap">
            <table className="dark-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-center">Minutes</th>
                  <th className="text-center">Rate / 1000 min</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Audio streaming</td>
                  <td className="text-center">{fmtMin(s.audioSeconds)}</td>
                  <td className="text-center">{money(RATES.audioPer1000Min)}</td>
                  <td style={{ fontWeight: 700, color: '#ff6b81' }}>{money(s.audioCost)}</td>
                </tr>
                <tr>
                  <td>Video streaming</td>
                  <td className="text-center">{fmtMin(s.videoSeconds)}</td>
                  <td className="text-center">{money(RATES.videoPer1000Min)}</td>
                  <td style={{ fontWeight: 700, color: '#ff6b81' }}>{money(s.videoCost)}</td>
                </tr>
                {s.gameFee > 0 && (
                  <tr>
                    <td>Gaming Server + Redis</td>
                    <td className="text-center">—</td>
                    <td className="text-center">flat / cycle</td>
                    <td style={{ fontWeight: 700, color: '#ff6b81' }}>{money(s.gameFee)}</td>
                  </tr>
                )}
                {s.hostingFee > 0 && (
                  <tr>
                    <td>Hosting Server</td>
                    <td className="text-center">—</td>
                    <td className="text-center">flat / cycle</td>
                    <td style={{ fontWeight: 700, color: '#ff6b81' }}>{money(s.hostingFee)}</td>
                  </tr>
                )}
                {s.filterFee > 0 && (
                  <tr>
                    <td>Filter</td>
                    <td className="text-center">—</td>
                    <td className="text-center">flat / cycle</td>
                    <td style={{ fontWeight: 700, color: '#ff6b81' }}>{money(s.filterFee)}</td>
                  </tr>
                )}
                {(s.customItems || []).map((it) => (
                  <tr key={it.id}>
                    <td>{it.label}</td>
                    <td className="text-center">—</td>
                    <td className="text-center">one-time</td>
                    <td style={{ fontWeight: 700, color: '#ff6b81' }}>{money(it.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Total due</td>
                  <td style={{ fontWeight: 800, color: '#ff6b81' }}>{money(s.totalCost)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Payment details */}
          <div style={{
            marginTop: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 12, padding: '14px 16px',
          }}>
            <div style={{ ...labelStyle, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payment details</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}>
              {PAYMENT.bankName}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
              {PAYMENT.accountName} · A/C: {PAYMENT.bankAccount}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
              Branch: {PAYMENT.branch} · Routing: {PAYMENT.routing} · SWIFT: {PAYMENT.swift}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
              Payment due within {PAY_TERMS_DAYS} days of the invoice issue date.
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

// ───────────────────────── PDF invoice ─────────────────────────
// Load an image URL into a PNG data URL + natural size (for jsPDF addImage).
function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataURL: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function buildInvoicePDF(data) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 44;
  const innerW = W - 2 * M;

  // Palette
  const pink = [255, 77, 109];
  const fuchsia = [192, 38, 211];
  const purple = [124, 58, 237];
  const green = [22, 163, 74];
  const red = [220, 38, 38];
  const violet = [139, 92, 246];
  const ink = [33, 37, 48];
  const dim = [120, 120, 130];
  const cur = data.rates.currency;

  const min = (sec) => (Number(sec || 0) / 60).toLocaleString('en-US', { maximumFractionDigits: 1 });
  const usd = (v) => `${cur} ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtD = (d) => new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);

  // Smooth horizontal gradient as a stack of thin rects.
  const hGrad = (x, y, w, h, c1, c2, steps = 80) => {
    const sw = w / steps;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      doc.setFillColor(
        Math.round(c1[0] + (c2[0] - c1[0]) * t),
        Math.round(c1[1] + (c2[1] - c1[1]) * t),
        Math.round(c1[2] + (c2[2] - c1[2]) * t),
      );
      doc.rect(x + i * sw, y, sw + 0.6, h, 'F');
    }
  };
  const fit = (im, mw, mh) => { const r = Math.min(mw / im.w, mh / im.h); return { w: im.w * r, h: im.h * r }; };

  const s = data.summary;
  const issueDate = new Date(data.periodEnd);
  const dueDate = new Date(issueDate.getTime() + PAY_TERMS_DAYS * 86400000);
  const invNo = `INV-${data.cycle}`;

  const [sellerImg, buyerImg] = await Promise.all([loadImage(SELLER.logo), loadImage(BILL_TO.logo)]);

  // ══ Header gradient band ══
  const HEAD = 128;
  hGrad(0, 0, W, HEAD, pink, purple);
  hGrad(0, HEAD, W, 4, fuchsia, [88, 28, 200]); // thin accent under the band

  // Seller logo on a white badge
  if (sellerImg) {
    doc.setFillColor(255, 255, 255).roundedRect(M, 30, 52, 52, 10, 10, 'F');
    const f = fit(sellerImg, 42, 42);
    doc.addImage(sellerImg.dataURL, 'PNG', M + 6 + (42 - f.w) / 2, 36 + (42 - f.h) / 2, f.w, f.h);
  }
  const sx = M + (sellerImg ? 66 : 0);
  doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(255, 255, 255);
  doc.text(SELLER.name, sx, 52);
  doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(255, 224, 236);
  doc.text(SELLER.tagline, sx, 68);
  let shy = 82;
  SELLER.lines.forEach((l) => { doc.text(l, sx, shy); shy += 12; });
  doc.setTextColor(255, 255, 255).text(SELLER.web, sx, shy);

  // Invoice meta (right, white on gradient)
  doc.setFont('helvetica', 'bold').setFontSize(26).setTextColor(255, 255, 255);
  doc.text('INVOICE', W - M, 52, { align: 'right' });
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(255, 230, 240);
  const meta = [
    `Invoice No:  ${invNo}`,
    `Issue date:  ${fmtD(issueDate)}`,
    `Due date:  ${fmtD(dueDate)}`,
  ];
  let my = 72;
  meta.forEach((m) => { doc.text(m, W - M, my, { align: 'right' }); my += 15; });

  // STATUS badge — payment status is only ever set via the GTIT console, so
  // this reflects a real payment on record, not a self-reported flag from
  // the invoice's own owner. Always shown once finalized (both states).
  if (data.finalized) {
    const isPaid = data.paymentStatus === 'paid';
    const bw = 100, bh = 22;
    const bx = W - M - bw, by = my + 3;
    doc.setFillColor(...(isPaid ? green : red)).roundedRect(bx, by, bw, bh, 6, 6, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(10.5).setTextColor(255, 255, 255);
    doc.text(`STATUS: ${isPaid ? 'PAID' : 'UNPAID'}`, bx + bw / 2, by + bh / 2 + 4, { align: 'center' });
  }

  // ══ Bill-to + period cards ══
  let y = HEAD + 28;
  const cardW = (innerW - 16) / 2;
  const cardH = 80;
  // Bill To (left, light pink tint)
  doc.setFillColor(253, 242, 246).roundedRect(M, y, cardW, cardH, 8, 8, 'F');
  doc.setFont('helvetica', 'bold').setFontSize(8.5).setTextColor(...fuchsia).text('BILL TO', M + 14, y + 18);
  let bx = M + 14;
  if (buyerImg) {
    const f = fit(buyerImg, 26, 26);
    doc.addImage(buyerImg.dataURL, 'PNG', M + 14, y + 26, f.w, f.h);
    bx = M + 14 + 34;
  }
  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...ink).text(BILL_TO.name, bx, y + 38);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...dim);
  let blY = y + 50;
  BILL_TO.lines.forEach((l) => { doc.text(l, bx, blY); blY += 11; });
  doc.setTextColor(...purple).text(BILL_TO.web, bx, blY);

  // Period (right, light purple tint)
  const px = M + cardW + 16;
  doc.setFillColor(244, 243, 255).roundedRect(px, y, cardW, cardH, 8, 8, 'F');
  doc.setFont('helvetica', 'bold').setFontSize(8.5).setTextColor(...purple).text('BILLING PERIOD', px + 14, y + 18);
  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...ink).text(data.label, px + 14, y + 38);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...dim);
  doc.text('Service: Media server usage', px + 14, y + 54);
  doc.text('(audio / video streaming)', px + 14, y + 66);

  y += cardH + 26;

  // ══ Line items ══
  const cols = [innerW * 0.46, innerW * 0.18, innerW * 0.18, innerW * 0.18];
  const colX = [M, M + cols[0], M + cols[0] + cols[1], M + cols[0] + cols[1] + cols[2]];
  const cellRight = (i, text) => doc.text(String(text), colX[i] + cols[i] - 12, y + 18, { align: 'right' });

  hGrad(M, y, innerW, 28, purple, pink);
  doc.setFont('helvetica', 'bold').setFontSize(9.5).setTextColor(255, 255, 255);
  doc.text('Description', colX[0] + 14, y + 18);
  doc.text('Minutes', colX[1] + cols[1] - 12, y + 18, { align: 'right' });
  doc.text('Rate / 1000 min', colX[2] + cols[2] - 12, y + 18, { align: 'right' });
  doc.text('Amount', colX[3] + cols[3] - 12, y + 18, { align: 'right' });
  y += 28;

  const rows = [
    { label: 'Audio streaming', dot: green, m: min(s.audioSeconds), rate: usd(data.rates.audioPer1000Min), amt: usd(s.audioCost) },
    { label: 'Video streaming', dot: violet, m: min(s.videoSeconds), rate: usd(data.rates.videoPer1000Min), amt: usd(s.videoCost) },
  ];
  if (s.gameFee > 0) rows.push({ label: 'Gaming Server + Redis', dot: pink, m: '—', rate: 'flat / cycle', amt: usd(s.gameFee) });
  if (s.hostingFee > 0) rows.push({ label: 'Hosting Server', dot: fuchsia, m: '—', rate: 'flat / cycle', amt: usd(s.hostingFee) });
  if (s.filterFee > 0) rows.push({ label: 'Filter', dot: purple, m: '—', rate: 'flat / cycle', amt: usd(s.filterFee) });
  (s.customItems || []).forEach((it) => rows.push({ label: it.label, dot: red, m: '—', rate: 'one-time', amt: usd(it.amount) }));
  rows.forEach((r, ri) => {
    if (ri % 2 === 1) { doc.setFillColor(250, 250, 252).rect(M, y, innerW, 30, 'F'); }
    doc.setFillColor(...r.dot).circle(colX[0] + 16, y + 15, 3.4, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(...ink).text(r.label, colX[0] + 26, y + 18);
    doc.setFont('helvetica', 'normal').setTextColor(70);
    cellRight(1, r.m);
    cellRight(2, r.rate);
    doc.setFont('helvetica', 'bold').setTextColor(...pink);
    cellRight(3, r.amt);
    y += 30;
  });
  doc.setDrawColor(...purple).setLineWidth(1).line(M, y, W - M, y);
  doc.setLineWidth(0.2);
  y += 20;

  // ══ Totals ══
  const boxW = 220;
  const boxX = W - M - boxW;
  const row = (label, val, color) => {
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...(color || [70, 70, 80]));
    doc.text(label, boxX, y);
    doc.text(val, W - M, y, { align: 'right' });
    y += 18;
  };
  row('Subtotal', usd(s.totalCost));
  row('Tax (0%)', usd(0));
  y += 4;
  hGrad(boxX - 12, y - 14, boxW + 12, 30, pink, fuchsia);
  doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(255, 255, 255);
  doc.text('TOTAL DUE', boxX, y + 5);
  doc.text(usd(s.totalCost), W - M, y + 5, { align: 'right' });
  y += 40;

  // ══ Payment details ══
  // Height follows the line count so adding or dropping a bank line can't leave
  // text sitting outside the panel.
  const payLines = [
    `Bank: ${PAYMENT.bankName}   ·   Branch: ${PAYMENT.branch}`,
    `Account Name: ${PAYMENT.accountName}   ·   A/C: ${PAYMENT.bankAccount}`,
    `Routing Number: ${PAYMENT.routing}   ·   SWIFT: ${PAYMENT.swift}`,
  ];
  const payH = 24 + payLines.length * 15;
  doc.setFillColor(253, 242, 246).roundedRect(M, y, innerW, payH, 8, 8, 'F');
  doc.setFillColor(...pink).roundedRect(M, y, 4, payH, 2, 2, 'F'); // colored left rail
  doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(...fuchsia).text('PAYMENT DETAILS', M + 16, y + 18);
  doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(60);
  payLines.forEach((line, i) => doc.text(line, M + 16, y + 35 + i * 15));
  y += payH + 18;

  // ══ Notes ══
  doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(...ink).text('Notes', M, y);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(90);
  const notes = [
    `Invoice issued by ${SELLER.name} (${SELLER.web}) for media server usage by ${BILL_TO.name} (${BILL_TO.web}).`,
    `Sessions billed this period: ${(s.sessions || 0).toLocaleString()}.`,
    `Payment due within ${PAY_TERMS_DAYS} days of the issue date (${fmtD(dueDate)}).`,
  ];
  let ny = y + 14;
  notes.forEach((n) => { doc.text(n, M, ny); ny += 13; });

  // ══ Footer ══
  hGrad(0, H - 6, W, 6, pink, purple);
  doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(150);
  doc.text(`${SELLER.name} — auto-generated invoice · ${invNo}`, M, H - 20);
  doc.text('Thank you for your business.', W - M, H - 20, { align: 'right' });

  return doc;
}

export default Bill;
